import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import barba from "@barba/core";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", () => {
  "use strict";

  /* =========================================================
     0 · Helpers & flags
     ========================================================= */
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const html = document.documentElement;
  const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const FINE = matchMedia("(hover: hover) and (pointer: fine)").matches;
  const T = Object.assign(
    {
      required: "This field is required.",
      email: "Enter a valid email address.",
      phone: "Enter a valid phone number.",
      minLength: "Please enter at least %d characters.",
      choose: "Please choose an option.",
      received: "Received",
      thanks: "Thank you.",
      reply: "Thanks — we'll reply within one business day.",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      sending: "Sending…",
      close: "Close notification",
      fixFields: "Please check the highlighted fields.",
      failTitle: "Something went wrong",
      failText: "Your message could not be sent. Please try again in a moment.",
      forms: {},
    },
    window.kmI18n || {}
  );
  const DATA = Object.assign({ newsUrl: "" }, window.kmData || {});

  /* =========================================================
     1 · Toast notifications (global, survive page transitions)
     kmToast({ type: 'success' | 'error' | 'info', title, text })
     ========================================================= */
  const toastStack = document.createElement("div");
  toastStack.className = "toasts";
  toastStack.setAttribute("aria-live", "polite");
  toastStack.setAttribute("aria-atomic", "false");
  document.body.appendChild(toastStack);

  function toast({ type = "info", title = "", text = "", duration = 6000 } = {}) {
    // one toast per message — repeated clicks don't pile them up
    const dup = $$(".toast", toastStack).find((t) => t.dataset.key === `${type}|${title}`);
    if (dup) return () => {};
    const el = document.createElement("div");
    el.dataset.key = `${type}|${title}`;
    el.className = `toast toast--${type}`;
    el.setAttribute("role", type === "error" ? "alert" : "status");

    const mark = document.createElement("i");
    mark.className = "toast__mark";
    mark.setAttribute("aria-hidden", "true");

    const body = document.createElement("div");
    body.className = "toast__body";
    if (title) {
      const b = document.createElement("b");
      b.textContent = title;
      body.appendChild(b);
    }
    if (text) {
      const p = document.createElement("span");
      p.textContent = text;
      body.appendChild(p);
    }

    const close = document.createElement("button");
    close.type = "button";
    close.className = "toast__close";
    close.setAttribute("aria-label", T.close);
    close.textContent = "×";

    el.append(mark, body, close);
    toastStack.appendChild(el);

    let timer = 0;
    const dismiss = () => {
      clearTimeout(timer);
      if (RM) return el.remove();
      gsap.to(el, { xPercent: 110, opacity: 0, duration: 0.45, ease: "power3.in", onComplete: () => el.remove() });
    };
    close.addEventListener("click", dismiss);
    el.addEventListener("mouseenter", () => clearTimeout(timer));
    el.addEventListener("mouseleave", () => { timer = setTimeout(dismiss, 2500); });

    if (!RM) gsap.from(el, { xPercent: 110, opacity: 0, duration: 0.7, ease: "power4.out" });
    if (duration) timer = setTimeout(dismiss, duration);
    return dismiss;
  }
  window.kmToast = toast;

  // Real Contact Form 7 forms report through the same toasts.
  document.addEventListener("wpcf7mailsent", (e) => {
    const f = e.target.closest("[data-km-form]");
    const msg = (f && T.forms[f.dataset.kmForm]) || [T.thanks, T.reply];
    toast({ type: "success", title: msg[0], text: msg[1] });
  });
  document.addEventListener("wpcf7invalid", () => toast({ type: "error", title: T.fixFields }));
  ["wpcf7mailfailed", "wpcf7spam"].forEach((ev) =>
    document.addEventListener(ev, () => toast({ type: "error", title: T.failTitle, text: T.failText }))
  );

  /* =========================================================
     1b · Header: compact once scrolled, hides while reading down,
     returns on scroll up; a brass hairline shows reading progress.
     ========================================================= */
  const hdr = $(".hdr");
  const bar = $(".js-progress");
  let lastY = scrollY;
  let ticking = false;
  const onScroll = () => {
    ticking = false;
    const y = scrollY;
    const max = document.documentElement.scrollHeight - innerHeight;
    if (bar) bar.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
    if (hdr) {
      hdr.classList.toggle("is-compact", y > 80);
      const menuOpen = mnav && !mnav.hidden;
      const goingDown = y > lastY + 4;
      const goingUp = y < lastY - 4;
      if (goingDown && y > 400 && !menuOpen && !hdr.contains(document.activeElement)) hdr.classList.add("is-hidden");
      else if (goingUp || y < 400) hdr.classList.remove("is-hidden");
    }
    lastY = y;
  };
  window.addEventListener("scroll", () => {
    if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
  }, { passive: true });
  hdr && hdr.addEventListener("focusin", () => hdr.classList.remove("is-hidden"));
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".js-top")) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: RM ? "auto" : "smooth" });
  });

  /* =========================================================
     2 · Mobile menu + current-menu state
     ========================================================= */
  const burger = $(".js-burger");
  const mnav = $(".js-mnav");

  function closeMenu(focusBack) {
    if (!burger || !mnav || mnav.hidden) return;
    mnav.hidden = true;
    burger.setAttribute("aria-expanded", "false");
    burger.setAttribute("aria-label", T.openMenu);
    document.body.classList.remove("locked");
    if (focusBack) burger.focus();
  }

  if (burger && mnav) {
    burger.addEventListener("click", () => {
      if (!mnav.hidden) return closeMenu(true);
      mnav.hidden = false;
      burger.setAttribute("aria-expanded", "true");
      burger.setAttribute("aria-label", T.closeMenu);
      document.body.classList.add("locked");
      const first = $("a", mnav);
      if (first) first.focus();
      if (!RM) {
        gsap.from($$("a", mnav), { y: 28, opacity: 0, duration: 0.7, ease: "power3.out", stagger: 0.05, clearProps: "all" });
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeMenu(true);
    });
    matchMedia("(min-width:1200px)").addEventListener("change", (m) => {
      if (m.matches) closeMenu(false);
    });
  }

  // After a Barba swap the header stays put, so re-mark the active item.
  function updateNav(url, bodyClass) {
    const here = new URL(url, location.href);
    const isArticle = /\bsingle-post\b|\bblog\b/.test(bodyClass || "");
    const news = DATA.newsUrl ? new URL(DATA.newsUrl, location.href).pathname : "";
    $$(".nav li, .mnav li").forEach((li) => {
      const a = $("a", li);
      if (!a || li.classList.contains("nav-cta")) return;
      const path = new URL(a.href, location.href).pathname;
      const on = path === here.pathname || (isArticle && news && path === news);
      li.classList.toggle("current-menu-item", on);
    });
  }

  /* =========================================================
     3 · Curtain page transition (GSAP, driven by Barba)
     ========================================================= */
  const curtain = $(".curtain");
  if (curtain) gsap.set(curtain, { xPercent: -101 });

  const curtainIn = () =>
    new Promise((res) => {
      gsap.set(curtain, { pointerEvents: "auto" });
      gsap.fromTo(curtain, { xPercent: -101 }, { xPercent: 0, duration: 0.55, ease: "power3.inOut", onComplete: res });
      gsap.fromTo($("svg", curtain), { scale: 0.7, opacity: 0 }, { scale: 1, opacity: 0.9, duration: 0.5, delay: 0.15, ease: "power3.out" });
    });

  const curtainOut = () =>
    new Promise((res) => {
      gsap.to(curtain, {
        xPercent: 101, duration: 0.6, ease: "power3.inOut",
        onComplete: () => { gsap.set(curtain, { xPercent: -101, pointerEvents: "none" }); res(); },
      });
    });

  /* =========================================================
     4 · Magnetic buttons (desktop, delegated — survives swaps)
     ========================================================= */
  if (FINE && !RM) {
    const movers = new WeakMap();
    const get = (btn) => {
      if (!movers.has(btn)) {
        movers.set(btn, {
          x: gsap.quickTo(btn, "x", { duration: 0.5, ease: "power3.out" }),
          y: gsap.quickTo(btn, "y", { duration: 0.5, ease: "power3.out" }),
        });
      }
      return movers.get(btn);
    };
    document.addEventListener("pointermove", (e) => {
      const btn = e.target.closest && e.target.closest(".btn");
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const m = get(btn);
      m.x((e.clientX - (r.left + r.width / 2)) * 0.22);
      m.y((e.clientY - (r.top + r.height / 2)) * 0.3);
    });
    document.addEventListener("pointerout", (e) => {
      const btn = e.target.closest && e.target.closest(".btn");
      if (btn && !btn.contains(e.relatedTarget)) {
        const m = get(btn);
        m.x(0);
        m.y(0);
      }
    });
  }

  /* =========================================================
     5 · Brass ring cursor (desktop, motion allowed only)
     ========================================================= */
  if (FINE && !RM) {
    const ring = document.createElement("div");
    ring.className = "cur";
    ring.setAttribute("aria-hidden", "true");
    document.body.appendChild(ring);
    const qx = gsap.quickTo(ring, "x", { duration: 0.35, ease: "power3.out" });
    const qy = gsap.quickTo(ring, "y", { duration: 0.35, ease: "power3.out" });
    addEventListener("mousemove", (e) => {
      qx(e.clientX);
      qy(e.clientY);
      ring.classList.add("on");
    }, { passive: true });
    document.addEventListener("mouseleave", () => ring.classList.remove("on"));
    document.addEventListener("mouseover", (e) => {
      ring.classList.toggle("big", !!e.target.closest('a,button,[tabindex="0"],label,select,input,textarea'));
    });
  }

  /* =========================================================
     6 · Cookie consent (GPC respected). Choices live in
     localStorage under km-consent; reopen from the footer.
     ========================================================= */
  const ck = $("#ck");
  if (ck) {
    const opts = $("#ck-opts");
    const an = $("#ck-an");
    const ad = $("#ck-ad");
    const saveBtn = $('[data-ck="save"]', ck);
    const settingsBtn = $('[data-ck="settings"]', ck);
    const consent = { analytics: false, advertising: false };
    let stored = false;
    try {
      const s = JSON.parse(localStorage.getItem("km-consent") || "null");
      if (s) { Object.assign(consent, s); stored = true; }
    } catch (e) { /* storage blocked */ }
    if (navigator.globalPrivacyControl) consent.advertising = false;

    const showBanner = () => {
      ck.hidden = false;
      if (!RM) gsap.fromTo(ck, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" });
    };
    const saveConsent = () => {
      try { localStorage.setItem("km-consent", JSON.stringify(consent)); } catch (e) { /* storage blocked */ }
      if (RM) { ck.hidden = true; return; }
      gsap.to(ck, { y: 30, opacity: 0, duration: 0.4, ease: "power2.in", onComplete: () => { ck.hidden = true; } });
    };
    if (!stored) setTimeout(showBanner, 900);

    ck.addEventListener("click", (e) => {
      const b = e.target.closest("[data-ck]");
      if (!b) return;
      const k = b.dataset.ck;
      if (k === "all") { consent.analytics = true; consent.advertising = !navigator.globalPrivacyControl; saveConsent(); }
      if (k === "none") { consent.analytics = consent.advertising = false; saveConsent(); }
      if (k === "settings") {
        const wasHidden = opts.hidden;
        opts.hidden = !wasHidden;
        b.setAttribute("aria-expanded", String(wasHidden));
        saveBtn.hidden = !wasHidden;
        an.checked = consent.analytics;
        ad.checked = consent.advertising;
      }
      if (k === "save") { consent.analytics = an.checked; consent.advertising = ad.checked; saveConsent(); }
    });

    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-cookie-open]")) return;
      opts.hidden = false;
      saveBtn.hidden = false;
      settingsBtn.setAttribute("aria-expanded", "true");
      an.checked = consent.analytics;
      ad.checked = consent.advertising;
      showBanner();
      an.focus();
    });
  }

  /* =========================================================
     7 · Per-page modules (re-run for every Barba container)
     ========================================================= */
  let cleanups = [];

  // 7a · Reveal on scroll + count-up. Content is visible without JS.
  function initReveal(scope) {
    function countUp(el) {
      if (el.dataset.done || RM) return;
      el.dataset.done = 1;
      const to = +el.dataset.to;
      const pre = el.dataset.prefix || "";
      const suf = el.dataset.suffix || "";
      if (!to) return;
      const o = { v: 0 };
      gsap.to(o, {
        v: to, duration: 1.8, ease: "power4.out",
        onUpdate: () => { el.textContent = pre + Math.round(o.v).toLocaleString("en-US") + suf; },
      });
    }

    const els = $$(".rv", scope);
    if (RM || !("IntersectionObserver" in window)) {
      els.forEach((e) => e.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add("in");
        $$(".count", en.target).forEach(countUp);
        io.unobserve(en.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -5% 0px" });

    const reveal = (e) => {
      e.classList.add("in");
      $$(".count", e).forEach(countUp);
      io.unobserve(e);
    };
    els.forEach((e) => {
      const r = e.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) reveal(e);
      else io.observe(e);
    });
    // Failsafe: anything already on screen after a moment is shown, even if
    // the observer was throttled (background tab, slow paint).
    const guard = setTimeout(() => {
      els.forEach((e) => {
        const r = e.getBoundingClientRect();
        if (!e.classList.contains("in") && r.top < innerHeight && r.bottom > 0) reveal(e);
      });
    }, 1500);
    cleanups.push(() => clearTimeout(guard));
    cleanups.push(() => io.disconnect());
  }

  // 7b · Hero: intro timeline + "video-in-type" canvas texture
  function initHero(scope, introDelay) {
    const hero = $("#hero", scope);
    if (!hero) return;
    const wins = $$(".win", hero);

    const intro = introDelay !== null && introDelay !== undefined;
    if (intro && !RM) {
      const tl = gsap.timeline({ delay: introDelay, defaults: { ease: "power4.out" } });
      // Failsafe: if frames are throttled or something stalls, never leave
      // the hero blank.
      setTimeout(() => tl.progress(1), (introDelay + 5) * 1000);
      tl.from($$(".eyebrow", hero), { y: 20, opacity: 0, duration: 0.9 })
        .from($$(".hero-h .ln", hero), { yPercent: 55, opacity: 0, duration: 1.3, stagger: 0.14 }, "-=0.6")
        .from($$(".hero-foot > *", hero), { y: 30, opacity: 0, duration: 1, stagger: 0.12 }, "-=0.8");
      cleanups.push(() => tl.kill());
    }
    if (!wins.length) return;

    let TEX = null;
    let GRAIN = null;
    let heroVisible = true;
    let heroRAF = 0;
    let winOff = [];
    let texW = 0;
    let texH = 0;

    // The texture honours the Customizer's brass; the rest are fixed warm
    // light/oxblood tones, painted like a photograph rather than themed UI.
    const cssHex = (name, fallback) => {
      const v = getComputedStyle(html).getPropertyValue(name).trim();
      return /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
    };

    const makeTexture = () => {
      const W = 1280;
      const H = 720;
      const brass = cssHex("--primary-color", "#b08d57");
      const c = document.createElement("canvas");
      c.width = W;
      c.height = H;
      const g = c.getContext("2d");
      g.fillStyle = "#8c7760";
      g.fillRect(0, 0, W, H);
      const blobs = [
        [0.18, 0.3, 0.45, "#f3e7d2", 0.95], [0.72, 0.22, 0.4, "#d9b57c", 0.9], [0.5, 0.78, 0.5, "#e9d6b8", 0.85],
        [0.9, 0.7, 0.35, "#8e3a45", 0.7], [0.32, 0.72, 0.28, brass, 0.8], [0.05, 0.9, 0.3, "#5a4a3a", 0.6],
        [0.62, 0.45, 0.22, "#fff6e8", 0.8], [0.4, 0.1, 0.25, "#a0303f", 0.45],
      ];
      blobs.forEach(([x, y, r, col, a]) => {
        const gr = g.createRadialGradient(x * W, y * H, 0, x * W, y * H, r * W);
        gr.addColorStop(0, col);
        gr.addColorStop(1, col + "00");
        g.globalAlpha = a;
        g.fillStyle = gr;
        g.fillRect(0, 0, W, H);
      });
      g.globalAlpha = 0.18;
      g.save();
      g.translate(W / 2, H / 2);
      g.rotate(-0.5);
      for (let i = -8; i < 8; i++) {
        g.fillStyle = i % 2 ? "#fff4e0" : "#3b2f26";
        g.fillRect(i * 90, -H, 38, H * 2);
      }
      g.restore();
      g.globalAlpha = 1;
      const im = g.getImageData(0, 0, W, H);
      const d = im.data;
      for (let i = 0; i < d.length; i += 4) {
        const n = (Math.random() - 0.5) * 34;
        d[i] += n;
        d[i + 1] += n;
        d[i + 2] += n;
      }
      g.putImageData(im, 0, 0);
      TEX = c.toDataURL("image/jpeg", 0.8);

      const s = 160;
      const gc = document.createElement("canvas");
      gc.width = gc.height = s;
      const gg = gc.getContext("2d");
      const gi = gg.createImageData(s, s);
      for (let i = 0; i < gi.data.length; i += 4) {
        const v = Math.random() > 0.5 ? 255 : 0;
        gi.data[i] = gi.data[i + 1] = gi.data[i + 2] = v;
        gi.data[i + 3] = Math.random() * 38;
      }
      gg.putImageData(gi, 0, 0);
      GRAIN = gc.toDataURL("image/png");
    };

    const paintWins = (t) => {
      const rangeX = Math.max(0, texW - hero.clientWidth);
      const rangeY = Math.max(0, texH - hero.clientHeight);
      const s = RM ? 0 : t / 1000;
      const px = rangeX * (0.5 + 0.5 * Math.sin(s * 0.11));
      const py = rangeY * (0.5 + 0.5 * Math.sin(s * 0.07 + 1.3));
      const gx = RM ? 0 : (Math.floor(s * 12) * 37) % 160;
      const gy = RM ? 0 : (Math.floor(s * 12) * 53) % 160;
      wins.forEach((w, i) => {
        const [ox, oy] = winOff[i] || [0, 0];
        w.style.backgroundPosition = `${gx - ox}px ${gy - oy}px, ${-px - ox}px ${-py - oy}px`;
      });
    };

    const measureWins = () => {
      // measure at the resting position: the intro tween offsets the lines
      wins.forEach((w) => { w.style.backgroundSize = ""; });
      const hr = hero.getBoundingClientRect();
      texW = Math.max(hr.width * 1.5, 900);
      texH = (texW * 9) / 16;
      if (texH < hr.height * 1.3) {
        texH = hr.height * 1.3;
        texW = (texH * 16) / 9;
      }
      winOff = wins.map((w) => {
        const r = w.getBoundingClientRect();
        return [r.left - hr.left, r.top - hr.top];
      });
      wins.forEach((w) => { w.style.backgroundSize = `160px 160px, ${texW}px ${texH}px`; });
      paintWins(performance.now());
    };

    const heroLoop = (t) => {
      paintWins(t);
      heroRAF = requestAnimationFrame(heroLoop);
    };
    const heroState = () => {
      const run = !RM && TEX && heroVisible && !document.hidden;
      if (run && !heroRAF) heroRAF = requestAnimationFrame(heroLoop);
      if (!run && heroRAF) { cancelAnimationFrame(heroRAF); heroRAF = 0; }
    };

    try {
      makeTexture();
      wins.forEach((w) => {
        w.style.backgroundImage = `url(${GRAIN}), url(${TEX})`;
        w.classList.add("tex");
      });
    } catch (err) {
      return;
    }

    // Offsets are relative to the hero, so every word is a window into one
    // shared frame. Measure after the intro so transforms don't skew them.
    const settle = () => requestAnimationFrame(() => { measureWins(); heroState(); });
    if (intro && !RM) setTimeout(settle, (introDelay + 2.6) * 1000); else settle();

    let vis;
    if ("IntersectionObserver" in window) {
      vis = new IntersectionObserver((e) => { heroVisible = e[0].isIntersecting; heroState(); });
      vis.observe(hero);
    }
    let rz;
    const onResize = () => { clearTimeout(rz); rz = setTimeout(measureWins, 120); };
    window.addEventListener("resize", onResize, { passive: true });
    document.addEventListener("visibilitychange", heroState);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureWins);

    cleanups.push(() => {
      cancelAnimationFrame(heroRAF);
      heroRAF = 0;
      clearTimeout(rz);
      if (vis) vis.disconnect();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", heroState);
    });
  }

  // 7c · Scroll parallax on photo slots + the big crossroads numerals
  function initParallax(scope) {
    if (RM) return;
    $$(".photo", scope).forEach((photo) => {
      const inner = $("img", photo) || $(".silh", photo);
      if (!inner) return;
      gsap.fromTo(inner, { yPercent: -7, scale: 1.14 }, {
        yPercent: 7, scale: 1.14, ease: "none",
        scrollTrigger: { trigger: photo, start: "top bottom", end: "bottom top", scrub: true },
      });
    });
    $$(".split .idx", scope).forEach((idx) => {
      gsap.fromTo(idx, { yPercent: 14 }, {
        yPercent: -14, ease: "none",
        scrollTrigger: { trigger: idx.closest(".side"), start: "top bottom", end: "bottom top", scrub: true },
      });
    });
    $$(".closing h2, .gate", scope).forEach((el) => {
      gsap.from(el, {
        scale: 0.96, ease: "none",
        scrollTrigger: { trigger: el, start: "top 95%", end: "top 55%", scrub: true },
      });
    });
    cleanups.push(() => ScrollTrigger.getAll().forEach((t) => t.kill()));
  }

  // 7d · Accordion
  function initAccordion(scope) {
    $$("[data-acc] button", scope).forEach((b) => b.addEventListener("click", () => {
      const open = b.getAttribute("aria-expanded") === "true";
      const panel = document.getElementById(b.getAttribute("aria-controls"));
      b.setAttribute("aria-expanded", String(!open));
      if (!panel) return;
      if (open) { panel.hidden = true; return; }
      panel.hidden = false;
      if (!RM) gsap.from(panel, { height: 0, opacity: 0, duration: 0.5, ease: "power3.out", clearProps: "height,opacity" });
    }));
  }

  // 7e · Demo forms — fake submit with real validation + notifications.
  // Real Contact Form 7 forms are validated by the plugin; they only pick
  // up the data-km-form hook so their events raise the same toasts.
  function validateField(el) {
    const v = el.type === "checkbox" ? "" : el.value.trim();
    if (el.hasAttribute("data-req")) {
      if (el.type === "checkbox") { if (!el.checked) return T.required; }
      else if (!v) return el.tagName === "SELECT" ? T.choose : T.required;
    }
    if (!v) return "";
    if (el.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return T.email;
    if ((el.type === "tel" || el.hasAttribute("data-tel")) && !(/^[+()\-.\s\d]{7,20}$/.test(v) && v.replace(/\D/g, "").length >= 7)) return T.phone;
    const min = +el.dataset.min;
    if (min && v.length < min) return T.minLength.replace("%d", min);
    return "";
  }

  function setFieldState(el, msg) {
    const fld = el.closest(".fld");
    if (!fld) return;
    const err = fld.querySelector(".err");
    fld.classList.toggle("bad", !!msg);
    if (err) {
      if (!err.id) err.id = `${el.id || el.name}-err`;
      err.textContent = msg;
      if (msg) el.setAttribute("aria-describedby", err.id); else el.removeAttribute("aria-describedby");
    }
    el.setAttribute("aria-invalid", msg ? "true" : "false");
    if (msg && !RM) gsap.fromTo(fld, { x: -6 }, { x: 0, duration: 0.5, ease: "elastic.out(1,0.35)", clearProps: "x" });
  }

  function initForms(scope) {
    $$(".wpcf7", scope).forEach((wrap) => {
      const form = $("form", wrap);
      if (!form) return;
      // events from CF7 bubble from the .wpcf7 wrapper — tag it for the toasts
      wrap.dataset.kmForm = guessForm(form);
      // CF7 initialises on DOMContentLoaded only; re-init after a Barba swap
      if (scope !== document && window.wpcf7 && typeof window.wpcf7.init === "function") {
        try { window.wpcf7.init(form); } catch (e) { /* already initialised */ }
      }
    });

    $$("form[data-form]", scope).forEach((f) => {
      f.dataset.kmForm = f.dataset.form;
      const fields = () => $$("[data-req], [data-min], [data-tel], [type=email], [type=tel]", f);

      // validate on blur, then live while the field is flagged
      f.addEventListener("focusout", (e) => {
        if (fields().includes(e.target)) setFieldState(e.target, validateField(e.target));
      });
      f.addEventListener("input", (e) => {
        const fld = e.target.closest(".fld");
        if (fld && fld.classList.contains("bad")) setFieldState(e.target, validateField(e.target));
      });
      f.addEventListener("change", (e) => {
        if (e.target.tagName === "SELECT" && fields().includes(e.target)) setFieldState(e.target, validateField(e.target));
      });

      f.addEventListener("submit", (e) => {
        e.preventDefault();
        if (f.dataset.busy) return;
        let first = null;
        fields().forEach((el) => {
          const msg = validateField(el);
          setFieldState(el, msg);
          if (msg && !first) first = el;
        });
        if (first) {
          first.focus();
          toast({ type: "error", title: T.fixFields });
          return;
        }

        // Fake submission: busy state, ~1.2s "network", then success.
        // Append ?km-fail=1 to the URL to rehearse the failure path.
        const btn = $('[type="submit"]', f);
        const label = btn.firstChild.textContent;
        f.dataset.busy = 1;
        btn.disabled = true;
        btn.classList.add("is-loading");
        btn.firstChild.textContent = T.sending + " ";

        setTimeout(() => {
          delete f.dataset.busy;
          btn.disabled = false;
          btn.classList.remove("is-loading");
          btn.firstChild.textContent = label;

          if (/[?&]km-fail=1/.test(location.search)) {
            toast({ type: "error", title: T.failTitle, text: T.failText });
            return;
          }

          const msg = T.forms[f.dataset.form] || [T.thanks, T.reply];
          const ok = document.createElement("div");
          ok.className = "ok";
          ok.setAttribute("role", "status");
          ok.tabIndex = -1;
          const eyebrow = document.createElement("p");
          eyebrow.className = "eyebrow";
          eyebrow.textContent = T.received;
          const h = document.createElement("h3");
          h.textContent = msg[0];
          const p = document.createElement("p");
          p.textContent = msg[1];
          ok.append(eyebrow, h, p);

          const swap = () => {
            f.replaceWith(ok);
            ok.focus({ preventScroll: true });
            if (!RM) gsap.from(ok, { y: 24, opacity: 0, duration: 0.8, ease: "power3.out" });
            toast({ type: "success", title: msg[0], text: msg[1] });
          };
          if (RM) swap();
          else {
            // timer-driven so the swap can't stall if frames are throttled
            gsap.to(f, { opacity: 0, y: -12, duration: 0.35, ease: "power2.in" });
            setTimeout(swap, 380);
          }
        }, 1200);
      });
    });
  }

  function guessForm(form) {
    const id = (form.querySelector("[id^='a-'],[id^='b-'],[id^='c-']") || {}).id || "";
    return { a: "apply", b: "roster", c: "contact" }[id.charAt(0)] || "";
  }

  function initPage(scope, introDelay) {
    initReveal(scope);
    initHero(scope, introDelay);
    initParallax(scope);
    initAccordion(scope);
    initForms(scope);
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  function teardown() {
    cleanups.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
    cleanups = [];
    ScrollTrigger.getAll().forEach((t) => t.kill());
  }

  /* =========================================================
     8 · Barba: swap only the <main> container. Falls back to a
     normal page load for admin/login links, modified clicks,
     reduced motion, or any transition error.
     ========================================================= */
  const first = $('[data-barba="container"]');
  initPage(first || document, 0.1);

  if (first && curtain && !RM) {
    const scrollToTarget = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      const target = id && document.getElementById(id);
      if (target) target.scrollIntoView({ block: "start" }); else window.scrollTo(0, 0);
    };

    barba.init({
      timeout: 8000,
      prevent: ({ el }) =>
        !!el.closest("#wpadminbar") ||
        /\/wp-(admin|login)/.test(el.pathname || "") ||
        el.hasAttribute("data-barba-prevent") ||
        /\.(pdf|zip|docx?|xlsx?)$/i.test(el.pathname || ""),
      transitions: [{
        name: "curtain",
        async leave() {
          closeMenu(false);
          await curtainIn();
          teardown();
        },
        async enter(data) {
          const bodyClass = (/<body[^>]*class="([^"]*)"/.exec(data.next.html) || [])[1] || "";
          document.body.className = bodyClass;
          updateNav(data.next.url.href, bodyClass);
          scrollToTarget();
          initPage(data.next.container, 0.35);
          await curtainOut();
        },
      }],
    });

    // Anything that fails mid-transition degrades to a hard navigation.
    window.addEventListener("pageshow", (e) => {
      if (e.persisted && curtain) gsap.set(curtain, { xPercent: -101, pointerEvents: "none" });
    });
  }
});
